import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import LoggerContext from "../../../context/LoggerProvider";
import useRetryFetch from "../../../hooks/useRetryFetch";
import useProcess from "../../../hooks/useProcess";
import { useProductSearch } from "../../../hooks/useProductSearch";
import { config } from "../../../Environment";
import { toast } from "sonner";
import ProductCard from "../ProductCard";
import MarketingSections from "../MarketingSections";
import { Skeleton } from "../../ui/skeleton";
import { Separator } from "../../ui/separator";
import { Sparkles } from "lucide-react";

interface FiltersType {
  search: string;
  categories: string[];
  tags: string[];
  difficulty: string[];
  estimatedTimeMin: number | undefined;
  estimatedTimeMax: number | undefined;
  priceMin: number | undefined;
  priceMax: number | undefined;
  vendorId: string | undefined;
}

function Activities({ vendorId, filters, ragResults, ragLoading }: { vendorId?: string; filters: FiltersType; ragResults?: any[] | null; ragLoading?: boolean }) {
  const location = useLocation();
  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const { startProcess } = useProcess();
  const retryFetch = useRetryFetch();

  const { data: activityProducts = [], isLoading: loading } = useProductSearch({
    type: "activity",
    filters,
    vendorId,
  });

  async function tryActivity(product: any) {
    // Check if user has already tried this activity
    try {
      const query = new URLSearchParams();
      query.set('workflowId', product.data.activity.fiWorkflow);
      query.set('trialMode', 'true');
      const response = await retryFetch(`${config.API_BASE}/api/user/process/search?${query.toString()}`);
      const result = await response.json();

      if (Array.isArray(result.list) && result.list.length > 0) {
        toast.error("You already have an active trial for this activity. Please complete or purchase it.");
        return;
      }

      // Start trial process
      await startProcess({
        prescriptionId: product.data.activity.fiWorkflow,
        from: location,
        forward: location,
        trialMode: true,
        trialProductId: product._id,
        onError: () => {
          toast.error("An error occurred while starting the trial.");
        },
      });
    } catch (error) {
      Logger.error("Failed to start trial", error);
      toast.error("Failed to start trial. Please try again.");
    }
  }

  // Check if any filters are active
  const hasActiveFilters = useMemo(() =>
    filters.search || filters.categories.length > 0 || filters.tags.length > 0 || filters.difficulty.length > 0 || filters.vendorId || filters.estimatedTimeMin !== undefined || filters.estimatedTimeMax !== undefined || filters.priceMin !== undefined || filters.priceMax !== undefined,
    [filters]
  );

  // Determine which data source to use
  const isRagMode = ragResults !== null && ragResults !== undefined;
  const displayProducts = isRagMode ? ragResults : activityProducts;
  const displayLoading = isRagMode ? !!ragLoading : loading;

  return (
    <div>
      {/* RAG results indicator */}
      {isRagMode && !displayLoading && displayProducts.length > 0 && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-primary/5 border border-primary/10 rounded-lg">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm text-primary/80 font-medium">
            {t("ai_search_results") || "Showing AI-powered results"}
          </span>
          <span className="text-xs text-gray-500 ms-auto">
            {displayProducts.length} {t("results") || "results"}
          </span>
        </div>
      )}

      {/* Marketing Sections - Only show when no filters are active and not in RAG mode */}
      {!hasActiveFilters && !vendorId && !isRagMode && (
        <>
          <MarketingSections onTryActivity={tryActivity} />
          <div className="flex items-center gap-4 mt-10 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200/60 to-transparent" />
            <span className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200/60 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-primary/40" />
              <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">
                {t("all_activities") || "All Activities"}
              </span>
            </span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200/60 to-transparent" />
          </div>
        </>
      )}

      {/* Loading skeleton */}
      {displayLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white ring-1 ring-gray-200/70 shadow-sm overflow-hidden flex flex-col animate-pulse">
              <Skeleton className="h-40 w-full rounded-none bg-gradient-to-br from-gray-100 to-gray-200" />
              <div className="p-4 flex-1">
                <Skeleton className="h-4 w-3/4 rounded-lg bg-gray-100 mb-2" />
                <Skeleton className="h-3 w-2/5 rounded-lg bg-gray-100 mb-3" />
                <div className="flex gap-1.5 mb-3">
                  <Skeleton className="h-4 w-14 rounded-full bg-gray-100" />
                  <Skeleton className="h-4 w-18 rounded-full bg-gray-100" />
                </div>
                <Skeleton className="h-3 w-full rounded bg-gray-100 mb-1.5" />
                <Skeleton className="h-3 w-4/5 rounded bg-gray-100" />
              </div>
              <div className="px-4 py-3 bg-gray-50/80 flex gap-2">
                <Skeleton className="h-8 flex-1 rounded-lg bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!displayLoading && displayProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 mb-5 rounded-2xl bg-gradient-to-br from-primary/10 to-purple-100 flex items-center justify-center shadow-sm">
            <Sparkles className="w-7 h-7 text-primary/50" />
          </div>
          <p className="text-gray-700 font-semibold mb-1.5 text-base">
            {isRagMode
              ? t("no_ai_results") || "No matching results found"
              : hasActiveFilters
              ? t("no_products_found") || "No products match your filters"
              : t("marketplace_unavailable") || "No products available yet"}
          </p>
          <p className="text-gray-400 text-sm max-w-xs leading-relaxed">
            {isRagMode
              ? t("try_different_query") || "Try a different search query or browse all products"
              : hasActiveFilters
              ? t("try_different_filters") || "Try adjusting or clearing your filters"
              : t("check_back_later") || "Check back soon — new products are added regularly"}
          </p>
        </div>
      )}

      {/* Product Grid */}
      {!displayLoading && displayProducts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayProducts.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              onTryNow={() => tryActivity(product)}
              buttonLayout="horizontal"
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Activities;
